import { ensurePropOf } from '@farol-ds/react'
import { assignSubComponents } from '@farol-ds/react/utilities/internal'
import classNames from 'classnames'
import { forwardRef, TableHTMLAttributes } from 'react'

import { TableContext, TableContextProps } from './context'
import styles from './table.module.scss'
import TableBody from './table-body'
import TableCell from './table-cell'
import cellStyles from './table-cell.module.scss'
import TableColumnHeaderCell from './table-column-header-cell'
import TableFooter from './table-footer'
import TableHeader from './table-header'
import TableRow from './table-row'
import TableRowHeaderCell from './table-row-header-cell'

export type TableElement = HTMLTableElement
export const TableSizes = ['sm', 'md'] as const
export type TableSize = (typeof TableSizes)[number]

export interface TableProps extends TableHTMLAttributes<TableElement> {
  size?: TableSize // [TODO] make it responsive prop. @andersonba
}

const TableRoot = forwardRef<TableElement, TableProps>(function Table(props, forwardedRef) {
  const { className, children, size: sizeProp, ...rest } = props

  const size = ensurePropOf<TableSize>(TableSizes, sizeProp, 'md')

  const getCellStyles: TableContextProps['getCellStyles'] = (opts = {}) => {
    const { justify, noBorder = false } = opts
    return classNames(cellStyles.root, {
      [cellStyles.sizeSm]: size === 'sm',
      [cellStyles.noBorder]: noBorder,
      [cellStyles.justifyStart]: justify === 'start',
      [cellStyles.justifyCenter]: justify === 'center',
      [cellStyles.justifyEnd]: justify === 'end',
    })
  }

  const classes = classNames(styles.root, className)

  return (
    <TableContext.Provider value={{ size, getCellStyles }}>
      <table {...rest} className={classes} ref={forwardedRef}>
        {children}
      </table>
    </TableContext.Provider>
  )
})

export const Table = assignSubComponents('Table', TableRoot, {
  Header: TableHeader,
  Body: TableBody,
  Footer: TableFooter,
  Row: TableRow,
  Cell: TableCell,
  RowHeaderCell: TableRowHeaderCell,
  ColumnHeaderCell: TableColumnHeaderCell,
})

export * from './table-body'
export * from './table-cell'
export * from './table-column-header-cell'
export * from './table-footer'
export * from './table-header'
export * from './table-row'
export * from './table-row-header-cell'
